/**
 * ListeningExercise — dictation-style exercise format.
 *
 * The user hears a term via TTS and must type what they heard.
 * Grading reuses the same fuzzy matching as fill-in-the-blank (handled by
 * the backend's /answer endpoint — no frontend-only grading here).
 *
 * Constraints:
 * - Auto-plays TTS on mount (once per card)
 * - Replay button always available
 * - No text hint before submission — only the speaker icon and instruction
 * - After answering (correct or wrong): shows term, definition, example
 * - TTS unavailability is handled by the parent (LearnPage falls back to fill_blank)
 */
import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLocale } from '../../i18n/context'

// ── Speaker icon SVG ──────────────────────────────────────────────────────────

function SpeakerIcon({ speaking, size = 32 }: { speaking: boolean; size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={speaking ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: 'block' }}
    >
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" strokeOpacity={speaking ? 1 : 0.4} />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" strokeOpacity={speaking ? 1 : 0.4} />
    </svg>
  )
}

// ── Ripple animation injected once ───────────────────────────────────────────

function injectRippleStyles() {
  if (document.getElementById('listening-ripple-kf')) return
  const s = document.createElement('style')
  s.id = 'listening-ripple-kf'
  s.textContent = `
    @keyframes listeningRipple {
      0%   { transform: scale(1);    opacity: 0.5; }
      100% { transform: scale(1.85); opacity: 0; }
    }
  `
  document.head.appendChild(s)
}

// ── Props ─────────────────────────────────────────────────────────────────────

export type ListeningPhase =
  | 'idle'          // user typing, no verdict yet
  | 'correct'       // answered correctly — reveal term/def/example
  | 'wrong'         // answered wrong — ghost text + must retype
  | 'retype'        // user actively retyping the correct answer
  | 'retype-wrong'  // retype rejected, shake

interface Props {
  /** The term content — spoken via TTS and revealed after answering */
  term: string
  /** Definition side content (shown after answering for reinforcement) */
  definition?: string
  /** Example sentence (shown after answering for reinforcement) */
  example?: string
  phase: ListeningPhase
  /** Called with the user's typed answer when they press Check */
  onSubmit: (value: string) => void
  /** Called with retype value */
  onRetypeSubmit: (value: string) => void
  /** Called when user clicks Continue (correct / retype-noted) */
  onContinue: () => void
  /** Speaks `text`, calls onEnd when done */
  speak: (text: string, onEnd?: () => void) => void
  isSpeaking: boolean
  disabled?: boolean
}

export default function ListeningExercise({
  term,
  definition,
  example,
  phase,
  onSubmit,
  onRetypeSubmit,
  onContinue,
  speak,
  isSpeaking,
  disabled,
}: Props) {
  const { tr } = useLocale()
  const lrn = tr.learn

  const [value, setValue] = useState('')
  const [shaking, setShaking] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  // Guard so TTS auto-plays exactly once on mount (not on every re-render)
  const autoPlayedRef = useRef(false)

  useEffect(() => { injectRippleStyles() }, [])

  // Auto-play TTS once when the exercise mounts
  useEffect(() => {
    if (!autoPlayedRef.current && term) {
      autoPlayedRef.current = true
      speak(term)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reset value when entering idle or wrong (for retype)
  useEffect(() => {
    if (phase === 'idle' || phase === 'wrong') setValue('')
  }, [phase])

  // Shake on retype-wrong
  useEffect(() => {
    if (phase === 'retype-wrong') {
      setShaking(true)
      const t = setTimeout(() => setShaking(false), 500)
      return () => clearTimeout(t)
    }
  }, [phase])

  // Auto-focus input when active
  useEffect(() => {
    if (phase === 'idle' || phase === 'wrong' || phase === 'retype' || phase === 'retype-wrong') {
      setTimeout(() => inputRef.current?.focus(), 60)
    }
  }, [phase])

  const isRetype = phase === 'wrong' || phase === 'retype' || phase === 'retype-wrong'
  const isAnswered = phase === 'correct' || phase === 'wrong'
  const isInputActive = phase === 'idle' || phase === 'wrong' || phase === 'retype' || phase === 'retype-wrong'

  const borderColor =
    phase === 'correct' ? 'var(--success)'
    : (phase === 'wrong' || phase === 'retype-wrong') ? 'var(--danger)'
    : 'var(--border)'

  const bgTint =
    phase === 'correct' ? 'rgba(39, 174, 96, 0.06)'
    : (phase === 'wrong' || phase === 'retype-wrong') ? 'var(--danger-soft)'
    : undefined

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      if (!isRetype || value.trim()) handleSubmit()
    }
    if (e.key === 'Escape' && (phase === 'correct' || phase === 'retype')) {
      onContinue()
    }
  }

  function handleSubmit() {
    if (isRetype) {
      if (!value.trim()) return
      onRetypeSubmit(value)
    } else {
      onSubmit(value)
    }
  }

  return (
    <div style={{
      width: '100%', maxWidth: 640, margin: '0 auto',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24,
    }}>
      {/* ── Speaker zone ──────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
        {/* Animated speaker button */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* Ripple ring (only when actively speaking) */}
          {isSpeaking && (
            <div
              style={{
                position: 'absolute',
                inset: -12,
                borderRadius: '50%',
                border: '2px solid var(--accent)',
                animation: 'listeningRipple 1.1s ease-out infinite',
                pointerEvents: 'none',
              }}
            />
          )}
          <button
            onClick={() => speak(term)}
            disabled={isSpeaking}
            aria-label={isSpeaking ? lrn.speakingLabel : lrn.replayAudio}
            title={isSpeaking ? lrn.speakingLabel : lrn.replayAudio}
            style={{
              width: 72, height: 72, borderRadius: '50%',
              border: `2px solid ${isSpeaking ? 'var(--accent)' : 'var(--border)'}`,
              background: isSpeaking ? 'var(--accent-soft)' : 'var(--bg-surface)',
              color: isSpeaking ? 'var(--accent)' : 'var(--text-primary)',
              cursor: isSpeaking ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'border-color 0.2s, background 0.2s, color 0.2s',
              flexShrink: 0,
            }}
          >
            <SpeakerIcon speaking={isSpeaking} size={28} />
          </button>
        </div>

        {/* Instruction label */}
        {phase === 'idle' && (
          <p style={{
            margin: 0, fontSize: 13, fontWeight: 600,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)',
            textAlign: 'center',
          }}>
            {lrn.listenAndType}
          </p>
        )}
      </div>

      {/* ── Post-answer reveal: term + definition + example ───────────────── */}
      <AnimatePresence>
        {isAnswered && (
          <motion.div
            key="reveal"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{
              width: '100%',
              background: 'var(--bg-surface)',
              border: `1.5px solid ${phase === 'correct' ? 'var(--success)' : 'var(--danger)'}`,
              borderRadius: 16,
              padding: '20px 20px',
              display: 'flex', flexDirection: 'column', gap: 12,
            }}
          >
            {/* Term (the answer) */}
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: 'var(--text-secondary)',
                marginBottom: 6, fontFamily: 'var(--font-ui)',
              }}>
                {lrn.termLabel}
              </div>
              <div style={{
                fontSize: 28, fontFamily: 'var(--font-content)', fontWeight: 600,
                color: phase === 'correct' ? 'var(--success)' : 'var(--danger)',
                lineHeight: 1.2,
              }}>
                {term}
              </div>
            </div>

            {/* Definition */}
            {definition && (
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, textAlign: 'center' }}>
                <div style={{
                  fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: 'var(--text-secondary)',
                  marginBottom: 6, fontFamily: 'var(--font-ui)',
                }}>
                  {lrn.definitionLabel}
                </div>
                <p style={{
                  margin: 0, fontSize: 15, fontFamily: 'var(--font-content)',
                  color: 'var(--text-secondary)', lineHeight: 1.5,
                }}>
                  {definition}
                </p>
              </div>
            )}

            {/* Example */}
            {example && (
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, textAlign: 'center' }}>
                <div style={{
                  fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: 'var(--text-secondary)',
                  marginBottom: 6, fontFamily: 'var(--font-ui)',
                }}>
                  {lrn.exampleLabel}
                </div>
                <p style={{
                  margin: 0, fontSize: 16, fontFamily: 'var(--font-content)',
                  color: 'var(--text-primary)', lineHeight: 1.6, fontStyle: 'italic',
                }}>
                  {example}
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Input area ────────────────────────────────────────────────────── */}
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Label above input */}
        <div style={{
          fontSize: 12, fontWeight: 600, letterSpacing: '0.06em',
          textTransform: 'uppercase', color: 'var(--text-secondary)',
          fontFamily: 'var(--font-ui)',
        }}>
          {isRetype ? lrn.typeCorrectAnswer : lrn.yourAnswer}
        </div>

        {/* Ghost text (correct answer shown during retype) */}
        <AnimatePresence>
          {isRetype && term && (
            <motion.div
              key="ghost"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                padding: '10px 16px', borderRadius: 8,
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                fontSize: 16, fontFamily: 'var(--font-content)',
                color: 'var(--text-secondary)', opacity: 0.55,
                userSelect: 'none',
              }}
            >
              {term}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Input field */}
        <motion.div
          animate={shaking ? { x: [0, -10, 10, -7, 7, -4, 4, 0] } : { x: 0 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
        >
          <div style={{ position: 'relative' }}>
            <input
              ref={inputRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={disabled || !isInputActive}
              placeholder={isRetype ? '' : lrn.typeWhatYouHeard}
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '16px 20px', borderRadius: 16,
                border: `2px solid ${borderColor}`,
                backgroundColor: bgTint ?? 'var(--bg-surface)',
                fontSize: 20, fontFamily: 'var(--font-content)',
                color: 'var(--text-primary)', outline: 'none',
                transition: 'border-color 0.2s, background-color 0.2s',
                caretColor: 'var(--accent)',
              }}
            />

            {/* Flash ring on verdict */}
            <AnimatePresence>
              {(phase === 'correct' || phase === 'wrong') && (
                <motion.div
                  key={phase}
                  initial={{ opacity: 0.7 }}
                  animate={{ opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.35, ease: 'easeOut' }}
                  style={{
                    position: 'absolute', inset: -3, borderRadius: 19,
                    border: `3px solid ${phase === 'correct' ? 'var(--success)' : 'var(--danger)'}`,
                    pointerEvents: 'none',
                  }}
                />
              )}
            </AnimatePresence>

            {/* Result icon */}
            {(phase === 'correct' || phase === 'wrong') && (
              <span style={{
                position: 'absolute', right: 16, top: '50%',
                transform: 'translateY(-50%)', fontSize: 20,
              }}>
                {phase === 'correct' ? '✓' : '✗'}
              </span>
            )}
          </div>
        </motion.div>

        {/* Action buttons */}
        <AnimatePresence mode="wait">
          {phase === 'idle' && (
            <motion.button
              key="check"
              initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              onClick={handleSubmit}
              disabled={!!disabled}
              style={btnStyle(!!disabled)}
            >
              {lrn.checkBtn}
            </motion.button>
          )}

          {(phase === 'retype' || phase === 'retype-wrong') && (
            <motion.button
              key="retype-submit"
              initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              onClick={handleSubmit}
              disabled={!value.trim()}
              style={btnStyle(!value.trim())}
            >
              {lrn.confirmBtn}
            </motion.button>
          )}

          {phase === 'wrong' && (
            <motion.div
              key="wrong-hint"
              initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            >
              <p style={{
                margin: '0 0 8px', fontSize: 13, color: 'var(--danger)',
                fontFamily: 'var(--font-ui)', textAlign: 'center',
              }}>
                {lrn.incorrectTypeAbove}
              </p>
            </motion.div>
          )}

          {phase === 'correct' && (
            <motion.button
              key="continue"
              initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              onClick={onContinue}
              style={btnStyle(false)}
            >
              {lrn.continueBtn}
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

function btnStyle(disabled: boolean): React.CSSProperties {
  return {
    width: '100%', padding: '14px 24px', borderRadius: 50,
    border: 'none',
    backgroundColor: disabled ? 'var(--border)' : 'var(--accent-fill)',
    color: disabled ? 'var(--text-secondary)' : 'var(--text-on-accent)',
    fontSize: 15, fontWeight: 600, fontFamily: 'var(--font-ui)',
    cursor: disabled ? 'default' : 'pointer',
    transition: 'background-color 0.15s',
  }
}
